import { useMemo } from "react";

import { dateFormats, swaDate } from "@swa-ui/date";
import { getPersistedData, setItem } from "@swa-ui/persistence";

import { parseMultiStationField } from "./stationValue";

const BOOKING_STORAGE_KEY =
  "AirBookingSearchResultsSearchStore-analyticsTrackingData-promoToken-searchFormData-showStickyFooter-v1";
const LFC_STORAGE_KEY = "AirLowFareCalendarSearchStore-searchFormData-searchResults-v1";
const MWEB_STORAGE_KEY = "ShoppingSearchHistoryStore::searchRequests";

/**
 * Retrieves air booking form/search data from other apps and parses it into the correct shape. Only fields that
 * have values in the stored data will be returned. This handles the case of searching for flights, modifying search
 * criteria in a later step, and returning. All stored data from other pages will also be cleared, so this form's
 * selections take priority again on future modifications.
 * @returns {Object} Object containing the form data to be set
 */

export const useDefaultFormValuesFromStorage = () => {
  return useMemo(() => {
    const visionData = getPersistedData(BOOKING_STORAGE_KEY, "sessionStorage")?.searchFormData;
    const lfcData = getPersistedData(LFC_STORAGE_KEY, "sessionStorage")?.searchFormData;
    const mWebData = (getPersistedData(MWEB_STORAGE_KEY, "localStorage") ?? [])[0];

    setItem(BOOKING_STORAGE_KEY, {}, { storageType: "session" });
    setItem(LFC_STORAGE_KEY, {}, { storageType: "session" });
    setItem(MWEB_STORAGE_KEY, {}, { storageType: "local" });

    if (shouldUseVisionAdultPassengersCount(visionData)) {
      visionData.adultsCount = visionData.adultPassengersCount;
    }

    const storedData = {
      formData: {
        adultPassengersCount: visionData?.adultPassengersCount,
        departureDate: tryFormatDate(
          visionData?.departureDate ?? lfcData?.departureDate ?? mWebData?.departureDate
        ),
        destinationAirportCode:
          parseMultiStationField(visionData?.to, visionData?.destinationAirportCode) ??
          lfcData?.destinationAirportCode,
        showFareInPoints:
          (visionData?.fareType ?? lfcData?.currencyCode) &&
          (visionData?.fareType ?? lfcData?.currencyCode) === "POINTS",
        originationAirportCode:
          parseMultiStationField(visionData?.from, visionData?.originationAirportCode) ??
          lfcData?.originationAirportCode,
        passengerType: visionData?.passengerType ?? lfcData?.passengerType,
        promoCode: visionData?.promoCode ?? lfcData?.promoCode,
        returnAirportCode: visionData?.returnAirportCode ?? lfcData?.returnAirportCode,
        returnDate: tryFormatDate(
          visionData?.returnDate ?? lfcData?.returnDate ?? mWebData?.returnDate
        ),
        tripType: visionData?.tripType ?? lfcData?.tripType,
      },
      passengerData: visionData
        ? [
            parseInt(visionData?.adultsCount) || 0,
            parseInt(visionData?.teensCount) || 0,
            parseInt(visionData?.olderChildCount) || 0,
            parseInt(visionData?.youngerChildCount) || 0,
            parseInt(visionData?.lapInfantPassengersCount) || 0,
          ]
        : undefined,
    };

    Object.keys(storedData.formData).forEach(
      (key) => storedData.formData[key] === undefined && delete storedData.formData[key]
    );

    return storedData;
  }, []);

  function shouldUseVisionAdultPassengersCount(visionData) {
    return (
      !visionData?.adultsCount &&
      !visionData?.teensCount &&
      !visionData?.olderChildCount &&
      !visionData?.youngerChildCount &&
      visionData?.adultPassengersCount
    );
  }

  function tryFormatDate(date) {
    return date ? swaDate(date).format(dateFormats.default) : undefined;
  }
};
